/**
 * Avatar Library (admin) — CRUD editor for role-play avatars.
 *
 * Presets (is_preset=True) are read-only; org-custom avatars can be
 * created, edited, deleted.  Each avatar carries name/title/persona/
 * traits/DISC/buying_role/voice/system_prompt/example_lines.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save, X, Edit3, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
const BUYING_ROLES = [
    'Decision maker', 'Champion', 'Economic buyer', 'Influencer',
    'Technical buyer', 'End user', 'Blocker', 'Unknown',
];
const DISC_COLORS = { D: '#E5484D', I: '#F2A423', S: '#1DA063', C: '#3B82F6' };

function emptyForm() {
    return {
        name: '',
        title: '',
        company: '',
        avatar_initials: '',
        avatar_color: '#5D3FD3',
        persona_summary: '',
        traits: '',
        buying_role: 'Unknown',
        voice: 'nova',
        system_prompt: '',
        example_lines: '',
        disc: {
            primary: 'D',
            secondary: 'C',
            scores: { D: 75, I: 40, S: 30, C: 55 },
        },
    };
}

export default function AvatarLibrary() {
    const { getAuthHeader } = useAuth();
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // avatar_id or 'new' or null
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/role-play/avatars`, {
                headers: getAuthHeader(),
            });
            setAvatars(res.data?.avatars || []);
        } catch (e) {
            toast.error('Failed to load avatars');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const startEdit = (avatar) => {
        if (avatar === 'new') {
            setForm(emptyForm());
            setEditing('new');
        } else {
            setForm({
                ...emptyForm(),
                ...avatar,
                traits: (avatar.traits || []).join(', '),
                example_lines: (avatar.example_lines || []).join('\n'),
                disc: avatar.disc || emptyForm().disc,
            });
            setEditing(avatar.avatar_id);
        }
    };

    const save = async () => {
        if (!form.name.trim()) {
            toast.error('Name is required');
            return;
        }
        const payload = {
            name: form.name,
            title: form.title,
            company: form.company,
            avatar_initials: form.avatar_initials || form.name[0].toUpperCase(),
            avatar_color: form.avatar_color,
            persona_summary: form.persona_summary,
            traits: form.traits.split(',').map(t => t.trim()).filter(Boolean),
            buying_role: form.buying_role,
            voice: form.voice,
            system_prompt: form.system_prompt,
            example_lines: form.example_lines.split('\n').map(t => t.trim()).filter(Boolean),
            disc: form.disc,
        };
        setSaving(true);
        try {
            if (editing === 'new') {
                await axios.post(`${API}/role-play/avatars`, payload, {
                    headers: getAuthHeader(),
                });
                toast.success('Avatar created');
            } else {
                await axios.put(`${API}/role-play/avatars/${editing}`, payload, {
                    headers: getAuthHeader(),
                });
                toast.success('Avatar updated');
            }
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this avatar?')) return;
        try {
            await axios.delete(`${API}/role-play/avatars/${id}`, {
                headers: getAuthHeader(),
            });
            await load();
            toast.success('Avatar deleted');
        } catch (e) {
            toast.error('Delete failed');
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Avatar Library
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        AI personas for Role Play training — presets are global, custom avatars are per-org.
                    </p>
                </div>
                <button
                    onClick={() => startEdit('new')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800"
                >
                    <Plus className="w-4 h-4" />
                    New avatar
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {avatars.map(a => (
                        <div key={a.avatar_id} className="bg-white border border-stone-200 rounded-xl p-4 flex flex-col">
                            <div className="flex items-start gap-3 mb-3">
                                <div
                                    className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                                    style={{ background: `linear-gradient(135deg, ${a.avatar_color}, ${a.avatar_color}88)` }}
                                >{a.avatar_initials || a.name[0]}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <div className="text-sm font-bold truncate">{a.name}</div>
                                        {a.is_preset && (
                                            <Lock className="w-3 h-3 text-stone-400" title="Preset — read-only" />
                                        )}
                                    </div>
                                    <div className="text-xs text-stone-500 truncate">{a.title} · {a.company}</div>
                                </div>
                            </div>
                            <div className="text-xs italic mb-2 flex-1" style={{ color: '#4527A0' }}>
                                "{a.persona_summary}"
                            </div>
                            <div className="flex flex-wrap gap-1 mb-3">
                                {a.disc && (
                                    <span
                                        className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white"
                                        style={{ background: DISC_COLORS[a.disc.primary] }}
                                    >{a.disc.primary}</span>
                                )}
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                                    {a.buying_role}
                                </span>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                                    🔊 {a.voice}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                                <button
                                    onClick={() => startEdit(a)}
                                    disabled={a.is_preset}
                                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs font-semibold text-stone-600 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                    title={a.is_preset ? 'Presets are read-only' : 'Edit'}
                                >
                                    <Edit3 className="w-3 h-3" /> Edit
                                </button>
                                <button
                                    onClick={() => remove(a.avatar_id)}
                                    disabled={a.is_preset}
                                    className="flex items-center justify-center gap-1 px-2 py-1.5 rounded text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal editor */}
            {editing && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-stone-200 sticky top-0 bg-white">
                            <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {editing === 'new' ? 'New Avatar' : 'Edit Avatar'}
                            </h2>
                            <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-stone-100 rounded-lg">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <Field label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} />
                                <Field label="Title" value={form.title} onChange={v => setForm({ ...form, title: v })} />
                                <Field label="Company" value={form.company} onChange={v => setForm({ ...form, company: v })} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <Field label="Initials" value={form.avatar_initials} onChange={v => setForm({ ...form, avatar_initials: v.slice(0, 3).toUpperCase() })} placeholder="AT" />
                                <div>
                                    <Label>Color</Label>
                                    <input type="color" value={form.avatar_color} onChange={e => setForm({ ...form, avatar_color: e.target.value })}
                                           className="w-full h-9 rounded border border-stone-200" />
                                </div>
                                <SelectField label="Voice" value={form.voice} options={VOICES} onChange={v => setForm({ ...form, voice: v })} />
                                <SelectField label="Buying Role" value={form.buying_role} options={BUYING_ROLES} onChange={v => setForm({ ...form, buying_role: v })} />
                            </div>

                            <Field label="Persona summary"
                                value={form.persona_summary}
                                onChange={v => setForm({ ...form, persona_summary: v })}
                                placeholder="Impatient Decision maker" />

                            <Field label="Traits (comma-separated)"
                                value={form.traits}
                                onChange={v => setForm({ ...form, traits: v })}
                                placeholder="interrupts, bottom-line, skeptical" />

                            <div>
                                <Label>DISC</Label>
                                <div className="grid grid-cols-4 gap-2">
                                    {['D', 'I', 'S', 'C'].map(letter => (
                                        <div key={letter}>
                                            <label className="text-xs font-bold tracking-widest uppercase block mb-1"
                                                   style={{ color: DISC_COLORS[letter] }}>{letter}</label>
                                            <input
                                                type="number" min="0" max="100"
                                                value={form.disc.scores[letter]}
                                                onChange={e => setForm({
                                                    ...form,
                                                    disc: {
                                                        ...form.disc,
                                                        scores: { ...form.disc.scores, [letter]: Number(e.target.value) || 0 },
                                                    },
                                                })}
                                                className="w-full px-2 py-1.5 rounded border border-stone-200 text-sm"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-3 mt-2">
                                    <SelectField label="Primary" value={form.disc.primary} options={['D', 'I', 'S', 'C']}
                                        onChange={v => setForm({ ...form, disc: { ...form.disc, primary: v } })} />
                                    <SelectField label="Secondary" value={form.disc.secondary} options={['D', 'I', 'S', 'C']}
                                        onChange={v => setForm({ ...form, disc: { ...form.disc, secondary: v } })} />
                                </div>
                            </div>

                            <Textarea label="System prompt (advanced — supplied to GPT-4o at conversation time)"
                                value={form.system_prompt}
                                onChange={v => setForm({ ...form, system_prompt: v })}
                                rows={6}
                                placeholder="You are {name}, {title} at {company}. You are a high-D Decision maker..." />

                            <Textarea label="Example lines (one per line — seeds voice/register)"
                                value={form.example_lines}
                                onChange={v => setForm({ ...form, example_lines: v })}
                                rows={3}
                                placeholder={'Let me stop you there. What\'s the ROI in plain dollars?\nWe\'ve tried three of these. Why is yours different?'} />
                        </div>
                        <div className="flex items-center justify-end gap-2 p-5 border-t border-stone-200 sticky bottom-0 bg-white">
                            <button onClick={() => setEditing(null)}
                                className="px-4 py-2 rounded-lg text-sm font-semibold text-stone-600 hover:bg-stone-100">
                                Cancel
                            </button>
                            <button onClick={save} disabled={saving}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 disabled:opacity-50">
                                <Save className="w-4 h-4" />
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Label({ children }) {
    return <div className="text-xs font-bold tracking-widest uppercase text-stone-600 mb-1">{children}</div>;
}

function Field({ label, value, onChange, placeholder }) {
    return (
        <div>
            <Label>{label}</Label>
            <input
                type="text" value={value || ''} placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                className="w-full px-3 py-2 rounded border border-stone-200 text-sm"
            />
        </div>
    );
}

function Textarea({ label, value, onChange, placeholder, rows = 4 }) {
    return (
        <div>
            <Label>{label}</Label>
            <textarea
                value={value || ''} placeholder={placeholder} rows={rows}
                onChange={e => onChange(e.target.value)}
                className="w-full px-3 py-2 rounded border border-stone-200 text-sm font-mono"
            />
        </div>
    );
}

function SelectField({ label, value, options, onChange }) {
    return (
        <div>
            <Label>{label}</Label>
            <select value={value} onChange={e => onChange(e.target.value)}
                    className="w-full px-2 py-1.5 rounded border border-stone-200 text-sm bg-white">
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
